import { useState, useEffect } from 'react';
import { api, ChatChannelResponse } from '@/lib/api';
import { cn } from '@/lib/utils';
import { seededGradient } from '../utils';

const MESSAGE_PREVIEW_MAX_LENGTH = 50;

function stripHtmlToPlainText(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent?.trim() ?? '';
}

interface ChannelListItemProps {
  channel: ChatChannelResponse;
  isActive: boolean;
  onClick: () => void;
}

export function ChannelListItem({ channel, isActive, onClick }: ChannelListItemProps) {
  const displayName =
    channel.channelType === 'direct'
      ? channel.dmPartnerDisplayName || channel.name || channel.dmPartnerId || 'Direct Message'
      : channel.name || 'Unnamed Channel';

  const [resolvedAvatarUrl, setResolvedAvatarUrl] = useState<string | null>(
    channel.imageRef ?? null,
  );

  useEffect(() => {
    setResolvedAvatarUrl(channel.imageRef ?? null);
    if (!channel.imageRef && channel.avatarFileId) {
      api
        .getDownloadUrl(channel.avatarFileId)
        .then((r) => setResolvedAvatarUrl(r.downloadUrl))
        .catch(() => {});
    }
  }, [channel.channelId, channel.imageRef, channel.avatarFileId]);

  const lastMessagePreview = channel.lastMessage
    ? (() => {
        const plain = stripHtmlToPlainText(channel.lastMessage.content);
        const text = plain || 'File sent';
        return text.length > MESSAGE_PREVIEW_MAX_LENGTH
          ? text.substring(0, MESSAGE_PREVIEW_MAX_LENGTH) + '...'
          : text;
      })()
    : null;

  const lastMessageTime = channel.lastMessage
    ? formatRelativeTime(channel.lastMessage.createdAt)
    : null;

  const unread = channel.unreadCount ?? 0;
  const initial = displayName.charAt(0).toUpperCase() || '?';
  const hasLeft = channel.hasLeft ?? false;

  return (
    <button
      onClick={onClick}
      className={`cursor-pointer rounded-lg w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors group ${
        isActive ? 'bg-[#111827] text-[#FFF]' : 'hover:bg-foreground/10'
      } ${hasLeft ? 'opacity-60' : ''}`}
    >
      <div
        className="h-10 w-10 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center"
        style={!resolvedAvatarUrl ? { background: seededGradient(displayName) } : undefined}
      >
        {resolvedAvatarUrl ? (
          <img src={resolvedAvatarUrl} alt={displayName} className="w-full h-full object-cover" />
        ) : (
          <span className="text-sm font-medium text-white">{initial}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 min-w-0">
            <span
              className={cn(
                'text-sm font-medium truncate',
                isActive ? 'text-[#FFF]' : 'text-foreground',
              )}
            >
              {displayName}
            </span>
            {hasLeft && (
              <span className="flex-shrink-0 text-[10px] font-medium px-1 py-0.5 rounded bg-muted text-muted-foreground leading-none">
                Left
              </span>
            )}
          </div>
          {lastMessageTime && (
            <span
              className={cn(
                'text-xs flex-shrink-0 ml-2',
                isActive ? 'text-[#FFF]' : 'text-muted-foreground',
              )}
            >
              {lastMessageTime}
            </span>
          )}
        </div>
        {(lastMessagePreview || unread > 0) && (
          <div className="flex items-center gap-1 mt-0.5">
            {lastMessagePreview && (
              <p
                className={cn(
                  'text-xs truncate flex-1',
                  isActive ? 'text-[#FFF]' : 'text-muted-foreground',
                )}
              >
                {lastMessagePreview}
              </p>
            )}
            {unread > 0 && (
              <span
                className={cn(
                  'text-[10px] font-semibold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center flex-shrink-0',
                  isActive ? 'bg-background text-foreground' : 'bg-[#111827] text-white',
                )}
              >
                {unread}
              </span>
            )}
          </div>
        )}
      </div>
    </button>
  );
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
